/**
 * Connect — sensor series client (OMON-209 Phase B).
 *
 * Bridges the Firestore registry (Phase A: `loadSensorLanes` → which devices /
 * sensors a client has) to the BigQuery telemetry: calls the admin-or-planner
 * `coordinator_sensors` callable for a ±14-day fortnight downsampled to 30-min
 * buckets, then expands each lane into its renderable channels (sensor-types.ts)
 * over that window.
 *
 * The web client never touches FIREHOSE or its Python-repr strings — the CF
 * does the `ast.literal_eval` and returns parsed columns. See
 * `backlog/docs/OMON-209-bigquery-sensor-source.md`.
 */
import { getFunctions, httpsCallable, httpsCallableFromURL } from "firebase/functions";
import { app, db, firestoreDatabaseId } from "../firebase";
import { collection, getDocs, orderBy, query, where, Timestamp } from "firebase/firestore";
import type { SensorLane } from "./sensors";
import { channelsFor, type ChannelSpec } from "./sensor-types";
import type { RenderLane } from "./swimlane/timeline-renderer";

/** Default look-back (days). OMON-225: user-selectable up to the CF cap.
 *  Ed 2026-08-26: 3 — the window ladder moved to 1/3/7/14 (day-scale reading
 *  is the real use; 30/60 skewed archival). Deeper history still arrives
 *  lazily behind it (timeline-view's archive backfill, planned by
 *  archive-plan.ts), so nothing was lost by shortening the default. */
export const DEFAULT_WINDOW_DAYS = 3;

/** Local-midnight start-of-day (Flutter `getStartOfDay`). */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * The look-back window for a selectedDate: `[selected − Nd,
 * startOfDay(selected + Nd) + 24h]`, end clamped to now. With selectedDate =
 * now this is `[now − Nd, now]`: a ROLLING window, so "1 day" is the last 24
 * hours. It used to start at local midnight, which made "1 day" up to 48h of
 * yesterday-plus-today (Ed chose rolling, 2026-09-25). OMON-225: `windowDays`
 * is configurable (was a fixed 14) — kept named `fortnightWindow` for
 * call-site stability; "fortnight" is now just the default.
 */
export function fortnightWindow(
  selectedDate: Date,
  windowDays: number = DEFAULT_WINDOW_DAYS,
): { start: Date; end: Date } {
  const start = new Date(selectedDate.getTime() - windowDays * 86_400_000);
  const endRaw = new Date(
    startOfDay(new Date(selectedDate.getTime() + windowDays * 86_400_000)).getTime() +
      24 * 3_600_000,
  );
  const now = new Date();
  return { start, end: endRaw > now ? now : endRaw };
}

/** One downsampled bucket: timestamp + the CF's parsed FIREHOSE columns. */
interface BucketRow {
  t: string;
  f: Record<string, unknown>;
}
/**
 * One event occurrence, as the server classifies it.
 *
 * ☠️ This union must match what the server actually emits, which is
 * `app.outcomemonitoring/api/omon_api/routes/connect.py` (omon-api Cloud Run,
 * ADR-0033 slice 1) — NOT the identically-named Cloud Function. MONO-146 added
 * `"door"` there and this type was not updated with it, so for a while it
 * misdescribed the wire format. It still type-checked: `e.kind === spec.eventKind`
 * compares this against the WIDER union on ChannelSpec.eventKind, and overlapping
 * unions compare legally. Nothing caught it, and nothing would have.
 */
interface EventRow {
  t: string;
  /** button = emergency press · motion = motion trigger · push = notification
   *  sent · door = a door OPEN edge (MONO-146; transitions, not per-reading). */
  kind: "button" | "motion" | "push" | "door" | "matMotion" | "matWeight" | "bedExit";
}
/** OMON-214 per-device health summary, as returned by coordinator_sensors. */
interface DeviceHealthRaw {
  battery: number | null;
  rssi: number | null;
  lastSeen: string | null;
}
interface CoordinatorSensorsResponse {
  window: { start: string; end: string; intervalMin: number };
  devices: Record<string, BucketRow[]>;
  events: Record<string, EventRow[]>;
  health?: Record<string, DeviceHealthRaw>;
  bucketCounts: Record<string, number>;
}

/** Device health, dates parsed — what the swimlane gutter renders (OMON-215). */
export interface DeviceHealth {
  battery: number | null; // % (Shelly BLU only)
  rssi: number | null; // dBm signal (all classes)
  lastSeen: Date | null;
}
interface CoordinatorSensorsRequest {
  deviceIds: string[];
  start: string;
  end: string;
  intervalMin?: number;
}

// ADR-0033 slice 1 cutover: the series action is served by the omon-api Cloud
// Run service, which speaks the Firebase callable wire protocol — same request
// envelope, same auth, same errors; only the host changes. The iot-clients
// action (sensors.ts) deliberately stays on the Firebase callable this slice.
// ROLLBACK: swap back to httpsCallable(getFunctions(app, "europe-west2"),
// "coordinator_sensors") — the Functions copy stays deployed until OMON-283/284
// contracts it.
const OMON_API_SENSORS_URL =
  "https://omon-api-myhmkw6jkq-nw.a.run.app/v1/coordinator_sensors";

// OMON-346: omon-api is PRODUCTION-scoped — it refuses any env-claimed or
// tenant-issued token at its one auth seam (#1848). Non-prod builds
// therefore use the Firebase callable, whose series action validates the
// requested deviceIds against the caller's OWN environment registry. The
// build's database id is the environment (ADR-0038), so the split is a
// build-time constant, not a runtime probe.
const callable =
  firestoreDatabaseId === "(default)"
    ? httpsCallableFromURL<CoordinatorSensorsRequest, CoordinatorSensorsResponse>(
        getFunctions(app, "europe-west2"),
        OMON_API_SENSORS_URL,
      )
    : httpsCallable<CoordinatorSensorsRequest, CoordinatorSensorsResponse>(
        getFunctions(app, "europe-west2"),
        "coordinator_sensors",
      );

/** Coerce a (possibly string) reading value to a finite number, or null. */
function coerceNumber(v: unknown): number | null {
  if (typeof v === "number") return isNaN(v) ? null : v;
  if (typeof v === "string" && v.trim() !== "" && v !== "undefined") {
    const n = Number(v);
    return isNaN(n) ? null : n;
  }
  return null;
}

/** Pull a channel's value out of a bucket's parsed `f` map. */
function channelValue(f: Record<string, unknown>, spec: ChannelSpec): number | null {
  const col = f[spec.column];
  let raw: unknown;
  if (spec.field) {
    raw = col && typeof col === "object" ? (col as Record<string, unknown>)[spec.field] : undefined;
  } else {
    raw = col;
  }
  // State channels map their raw value (boolean / 'true'/'false' / window==0)
  // to 1 | 0 | null themselves; continuous channels coerce to a number.
  if (spec.stateOf) return spec.stateOf(raw);
  const n = coerceNumber(raw);
  if (n === null) return null;
  return spec.transform ? spec.transform(n) : n;
}

/** Build the hover-tooltip value formatter for a channel. */
function makeValueLabel(spec: ChannelSpec): (v: number | null) => string {
  if (spec.kind === "event") return () => "";
  if (spec.kind === "state") {
    const [off, on] = spec.stateLabels ?? ["Off", "On"];
    return (v) => (v === 1 ? on : v === 0 ? off : "—");
  }
  const unit = spec.unit;
  return (v) =>
    v === null ? "—" : `${Math.round(v * 10) / 10}${unit ? ` ${unit}` : ""}`;
}

/**
 * Resolve a client's lanes to render-ready swimlanes over the fortnight window.
 * One callable invocation fans out across every device the client has.
 */
export interface SensorSeriesResult {
  start: Date;
  end: Date;
  renderLanes: RenderLane[];
  hasData: boolean;
  /** OMON-214/215: per-device gutter health, keyed by deviceId. */
  health: Record<string, DeviceHealth>;
}

/** Bucket intervals (minutes) the CF accepts — keep in lock-step with the CF's
 *  `_ALLOWED_INTERVALS`. OMON-224 added 1/2 for deep zoom. */
const ALLOWED_INTERVALS = [1, 2, 5, 10, 15, 30, 60] as const;
/** Target number of buckets across the visible plot. The swimlane is ~1200px,
 *  so ~720 buckets ≈ one band per ~1.5px — finer than the eye resolves, so
 *  there's no visible gain (or point fetching more) past this density. */
const TARGET_BUCKETS = 720;

/**
 * OMON-224: pick a bucket interval (minutes) by PIXEL DENSITY, not a hand-tuned
 * span ladder. We choose the finest allowed interval that still yields ≲
 * `TARGET_BUCKETS` across the visible span, so the chart renders at ~screen
 * resolution at every zoom: ~30-min over a fortnight, ~2-min over a day, ~1-min
 * within a few hours. This supersedes the OMON-222 threshold ladder (a Flutter
 * `fetchOnePerInterval` fossil) — the old 30/15/10 steps left multi-day views
 * far coarser than the screen could show, hiding intra-bucket variation that
 * the pixels had room for. Sub-bucket spikes are always recoverable by zooming
 * (down to 1-min). Payload stays bounded: buckets ≈ TARGET_BUCKETS regardless
 * of span, since the interval scales with it.
 */
export function intervalForSpanMs(spanMs: number): number {
  const idealMin = spanMs / 60_000 / TARGET_BUCKETS;
  for (const iv of ALLOWED_INTERVALS) {
    if (iv >= idealMin) return iv;
  }
  return ALLOWED_INTERVALS[ALLOWED_INTERVALS.length - 1];
}

export async function loadSensorSeries(
  lanes: SensorLane[],
  selectedDate: Date,
  intervalMin?: number,
  windowDays?: number,
  onFirebaseLayered?: (lanes: RenderLane[]) => void,
): Promise<SensorSeriesResult> {
  const { start, end } = fortnightWindow(selectedDate, windowDays);
  return loadSensorSeriesWindow(lanes, start, end, intervalMin, onFirebaseLayered);
}

/**
 * Same projection as `loadSensorSeries` but over an EXPLICIT `[start, end]`
 * window — used by the connect view's incremental live poll (single-store, no
 * Firestore live-tail: see reference_omon_coordinator_single_store). Re-querying
 * a short recent slice and merging is cheap; the data store stays BigQuery only.
 */
/**
 * OMON-236: read operational "event" records (push-sent) from Firebase
 * _values/{collectionId}/values within [start, end]. agent.pushnotifications
 * writes these (pushNotificationSent:True, timestamp = send time) as a Firestore
 * op-record — they never reach the IoT firehose/BigQuery the CF queries, so the
 * lane is sourced directly here. Each doc → one scatter point. Resilient: a read
 * failure degrades to an empty lane rather than breaking the whole view.
 */
async function loadFirebaseEvents(
  valuesPath: string,
  start: Date,
  end: Date,
  opts: {
    tsField?: string;
    match?: { field: string; value: number | string | boolean };
    pointValue?: (doc: Record<string, unknown>) => number;
  } = {},
): Promise<{ t: Date; value: number | null }[]> {
  const tsField = opts.tsField ?? "timestamp";
  try {
    // 2026-07-08: the match filter is applied SERVER-SIDE. Client-side-only
    // filtering fetched every doc in the window — against the brain's
    // noise-stamped state records (~2.7k/day/device) that meant ~38k docs per
    // 14-day view: a 30s+ main-thread freeze and a lane that never painted.
    // Composite indexes values(deviceEventType,timestamp) and
    // values(window,deviceTimestamp) were created in prod for these shapes; if
    // an index is missing the query fails and the catch degrades to an empty
    // lane (with the console.warn below naming the path).
    const clauses = [
      ...(opts.match ? [where(opts.match.field, "==", opts.match.value)] : []),
      where(tsField, ">=", Timestamp.fromDate(start)),
      where(tsField, "<=", Timestamp.fromDate(end)),
      // DESC deliberately: the legacy Flutter-coordinator composite indexes
      // (pushNotificationSent/window + ts DESC) are READY in prod and serve this
      // shape today, whereas the ASC twin got wedged behind a deletion tombstone
      // (2026-07-08). Order doesn't matter downstream — we sort ascending below
      // and the renderer re-sorts per lane anyway.
      orderBy(tsField, "desc"),
    ];
    const snap = await getDocs(query(collection(db, valuesPath), ...clauses));
    const pts: { t: Date; value: number | null }[] = [];
    snap.forEach((d) => {
      const data = d.data() as Record<string, unknown>;
      if (opts.match && data[opts.match.field] !== opts.match.value) return;
      const ts = data[tsField] as { toDate?: () => Date } | undefined;
      if (ts && typeof ts.toDate === "function") {
        pts.push({ t: ts.toDate(), value: opts.pointValue ? opts.pointValue(data) : 1 });
      }
    });
    pts.sort((a, b) => a.t.getTime() - b.t.getTime());
    return pts;
  } catch (err) {
    console.warn("[connect] loadFirebaseEvents failed for", valuesPath, err);
    return [];
  }
}

export async function loadSensorSeriesWindow(
  lanes: SensorLane[],
  start: Date,
  end: Date,
  intervalMin?: number,
  // OMON-243: when provided (the initial load), _values-sourced lanes hydrate
  // off the critical path and this fires once they're layered in. Omitted on the
  // live poll / zoom refetch, where we await them so the result is complete.
  onFirebaseLayered?: (lanes: RenderLane[]) => void,
): Promise<SensorSeriesResult> {
  // Only fetch devices that have at least one renderable channel, deduped.
  const deviceIds = [
    ...new Set(lanes.filter((l) => channelsFor(l.propertyType).length > 0).map((l) => l.deviceId)),
  ];
  if (deviceIds.length === 0) {
    return { start, end, renderLanes: [], hasData: false, health: {} };
  }

  const res = await callable({
    deviceIds,
    start: start.toISOString(),
    end: end.toISOString(),
    ...(intervalMin ? { intervalMin } : {}),
  });
  const devices = res.data.devices ?? {};
  const events = res.data.events ?? {};

  // OMON-214: parse the per-device health summary (battery/rssi/lastSeen) for
  // the gutter. lastSeen ISO → Date; absent device → omitted (gutter shows —).
  const health: Record<string, DeviceHealth> = {};
  for (const [id, h] of Object.entries(res.data.health ?? {})) {
    health[id] = {
      battery: h.battery,
      rssi: h.rssi,
      lastSeen: h.lastSeen ? new Date(h.lastSeen) : null,
    };
  }

  const renderLanes: RenderLane[] = [];
  const pendingFirebase: {
    rl: RenderLane;
    valuesPath: string;
    opts: { tsField?: string; match?: { field: string; value: number | string | boolean } };
  }[] = [];
  let hasData = false;
  for (const lane of lanes) {
    const specs = channelsFor(lane.propertyType);
    if (specs.length === 0) continue;
    const buckets = devices[lane.deviceId] ?? [];
    const laneEvents = events[lane.deviceId] ?? [];
    // propertyPath = the value-collection ref path (valuesPath minus "/values"),
    // the first half of the OMON-212 filter key (matches the Flutter scheme).
    const propertyPath = lane.valuesPath.replace(/\/values$/, "");
    for (const spec of specs) {
      let points: { t: Date; value: number | null }[];
      let firebaseOpts: {
        tsField?: string;
        match?: { field: string; value: number | string | boolean };
        pointValue?: (doc: Record<string, unknown>) => number;
      } | null = null;
      if (spec.kind === "event" && spec.firebaseEvents) {
        // OMON-236: operational events (push-sent, door opens) live in Firebase
        // _values, written by agent.pushnotifications / the per-EoC brain — the IoT
        // firehose/BigQuery the CF reads has no record of them.
        // OMON-243: start EMPTY and hydrate from _values after first paint (see the
        // pendingFirebase pass below) so the initial render isn't blocked on these
        // reads alongside the BigQuery query.
        points = [];
        firebaseOpts = { tsField: spec.firebaseTsField, match: spec.firebaseMatch, pointValue: spec.firebaseValue };
      } else if (spec.kind === "event") {
        // Event lanes draw the CF's occurrences (scatter), not bucket values.
        points = laneEvents
          .filter((e) => e.kind === spec.eventKind)
          .map((e) => ({ t: new Date(e.t), value: 1 }));
        if (points.length) hasData = true;
      } else {
        points = buckets.map((b) => ({ t: new Date(b.t), value: channelValue(b.f, spec) }));
        // OMON-235: state lanes HOLD their last known value across buckets that
        // lack it. A Shelly BLU door/window reports `window` only in its full
        // BTHome sensor packets; the interleaved battery-only beacons omit it, so
        // a bucket that samples a beacon has no state. A door's state persists
        // until it next changes, so forward-fill it — otherwise the lane reads grey
        // between sparse readings even though the door hasn't moved. (Gradient
        // lanes are NOT filled: there a gap genuinely means "no reading".) Grey
        // remains only BEFORE the first known reading.
        if (spec.kind === "state") {
          let last: number | null = null;
          for (const p of points) {
            if (p.value !== null) last = p.value;
            else if (last !== null) p.value = last;
          }
        }
        if (points.some((p) => p.value !== null)) hasData = true;
      }
      const rl: RenderLane = {
        deviceId: lane.deviceId,
        deviceType: lane.deviceType,
        deviceImage: lane.deviceImage,
        deviceName: lane.deviceName,
        topLabel: lane.location,
        subLabel: spec.unit ? `${spec.label} (${spec.unit})` : spec.label,
        filterKey: `${propertyPath}::${spec.subtype}`,
        kind: spec.kind,
        // OMON-295: default diverging, so an unreviewed channel keeps today's look.
        palette: spec.palette ?? "diverging",
        colorFor: spec.colorFor,
        valueLabel: makeValueLabel(spec),
        points,
      };
      renderLanes.push(rl);
      if (firebaseOpts) pendingFirebase.push({ rl, valuesPath: lane.valuesPath, opts: firebaseOpts });
    }
  }

  // OMON-243: hydrate the _values-sourced lanes. With onFirebaseLayered (initial
  // load) do it OFF the critical path — return the BigQuery lanes now, read
  // _values in the background, then call back to re-render. Without it (live poll
  // / zoom refetch) await so the returned result is already complete.
  if (pendingFirebase.length) {
    const hydrate = async () => {
      await Promise.all(
        pendingFirebase.map(async (pf) => {
          pf.rl.points = await loadFirebaseEvents(pf.valuesPath, start, end, pf.opts);
        }),
      );
    };
    if (onFirebaseLayered) {
      void hydrate()
        .then(() => onFirebaseLayered(renderLanes))
        .catch((err) => console.warn("[connect] firebase lane hydration failed", err));
    } else {
      await hydrate();
    }
  }

  return { start, end, renderLanes, hasData, health };
}
